import * as ms from "resmed-mugshot-image-analyzer";

import isMouthOpen from "./MouthOpenAnalysis";


import { GLASSES_CHECK_ENABLED } from "./MugShotConfig";

/**
 *
 * @param tf the tensor flow engine
 * @param img the image to inspect for mouth open
 * @param model the face detection model to use
 *
 * @returns {Promise<boolean>} true if the person's mouth was open, false otherwise.
 */
export const isMouthOpenInImage = async (tf, img, model ) => {
    let imageStatus = await getFaceAttributes(tf, img, model, false, false);
    return imageStatus.mouthOpen;
}

/**
 *
 * @param tf the tensor flow engine
 * @param img the image to inspect for mouth open (and eyes closed and glasses if requested)
 * @param model the face detection model to use
 * @param checkEyes should we check if eyes are closed?
 * @param detectGlasses should we check if the person is wearing glasses?  Make sure you pass a model the glasses model
 * enabled to use this.
 *
 * @returns {Promise<{x: integer, y:integer, mouthOpen: boolean, eyesClosed: boolean, glasses: boolean}>}
 * A structure with boolean values for mouth open, eyes closed,glasses on, and the x and y angle of the person's face.
 */
export const getFaceAttributes = async (tf, img, model, checkEyes = false, detectGlasses = false ) => {

    for (let frameNum = 0; frameNum < 4; frameNum++) {
        // burn some frames so we get a solid result
        await model.estimateFaces({input: img,});
    }

    let returnStruct = {
        x: -1000,
        y: -1000,
        mouthOpen: true,
        eyesClosed: true,
        glasses: false
    }

    const predictions = await model.estimateFaces({ input: img, detectGlasses: detectGlasses });
    const prediction = predictions[0];

    if(prediction) {
        const fmesh = new ms.FaceIrisMesh();
        fmesh.updatePoints(prediction.scaledMesh);

        const landmarks = ms.getLandmarks(true, true);
        let lcsm = ms.getLandmarkCoordinates(fmesh, landmarks);

        returnStruct.mouthOpen = isMouthOpen(lcsm);
        returnStruct.glasses = prediction.glasses;

        let lcsmScaled = ms.landmarksCopy(lcsm);
        const [, eulerAngles] = ms.getHeadPose(lcsmScaled);
        const eulerAnglesDeg = ms.getEulerAnglesDeg(eulerAngles);

        returnStruct.x = eulerAnglesDeg.x.toFixed(2);
        returnStruct.y = eulerAnglesDeg.y.toFixed(2);

        if ( checkEyes ) {
            // Scale and flip the landmark coordinates
            let lcsmScaled = ms.landmarksCopy(lcsm);

            const irisShape = ms.getIrisShape(lcsm);

            ms.scaleLandmarkCoordinates(lcsmScaled, irisShape.scaleFactor);
            let visibility = ms.getIrisVisibility(lcsmScaled);

            returnStruct.eyesClosed = !(visibility.left.irisVisible || visibility.right.irisVisible);
        }

        return returnStruct;
    } else {
        return returnStruct;
    }

}

/**
 * Similar to getFaceAttributes, however, it does not do any burn frames for accuracy and always does glasses and eyes
 * detection.  This is used for post processing on a timeout, to get some data that can be logged with the
 * scan-fail operation.
 *
 * @param tf the tensor flow engine
 * @param img the image to inspect for mouth open (and eyes closed and glasses if requested)
 * @param model the face detection model to use
 *
 * @returns {Promise<{glasses: boolean, mouthOpen: boolean, x: number, y: number, eyesClosed: boolean}>}
 * A structure with boolean values for mouth open, eyes closed,glasses on, and the x and y angle of the person's face.
 */
export const getFaceInfo = async (tf, img, model ) => {

    let returnStruct =
        {
            x: -1000,
            y: -1000,
            mouthOpen: true,
            eyesClosed: true,
            glasses: false
        }

    const predictions = await model.estimateFaces({ input: img, detectGlasses: GLASSES_CHECK_ENABLED });
    const prediction = predictions[0];

    if(prediction) {
        const fmesh = new ms.FaceIrisMesh();
        fmesh.updatePoints(prediction.scaledMesh);

        const landmarks = ms.getLandmarks(true, true);
        let lcsm = ms.getLandmarkCoordinates(fmesh, landmarks);

        returnStruct.mouthOpen = isMouthOpen(lcsm);
        returnStruct.glasses = prediction.glasses;


        // Scale and flip the landmark coordinates
        let lcsmScaled = ms.landmarksCopy(lcsm);
        const [, eulerAngles] = ms.getHeadPose(lcsmScaled);
        const eulerAnglesDeg = ms.getEulerAnglesDeg(eulerAngles);

        returnStruct.x = eulerAnglesDeg.x.toFixed(2);
        returnStruct.y = eulerAnglesDeg.y.toFixed(2);

        const irisShape = ms.getIrisShape(lcsm);

        ms.scaleLandmarkCoordinates(lcsmScaled, irisShape.scaleFactor);
        let visibility = ms.getIrisVisibility(lcsmScaled);

        returnStruct.eyesClosed = !(visibility.left.irisVisible || visibility.right.irisVisible);

        return returnStruct;
    } else {
        return returnStruct;
    }

}